import Vue from 'vue';
import firebase, { database } from '@/plugins/firebase';
import store from '@/store';
import { callFn } from '@/utils/functionsClient';
import { coversDate } from '@/utils/contractPeriod';
export const ContractTemplatesCollection = 'contractTemplates';
export const ContractsCollection = 'contracts';
function upsert(list, item) {
    const index = list.findIndex(x => x.id === item.id);
    if (index === -1) {
        list.push(item);
        return;
    }
    Vue.set(list, index, item);
}
function remove(list, id) {
    const index = list.findIndex(x => x.id === id);
    if (index !== -1)
        list.splice(index, 1);
}
export const ContractModule = {
    namespaced: true,
    state: {
        templates: [],
        contracts: [],
    },
    mutations: {
        storeTemplate(state, template) {
            upsert(state.templates, template);
        },
        removeTemplate(state, id) {
            remove(state.templates, id);
        },
        storeContract(state, contract) {
            upsert(state.contracts, contract);
        },
        removeContract(state, id) {
            remove(state.contracts, id);
        },
        clear(state) {
            state.templates.splice(0);
            state.contracts.splice(0);
        },
    },
    actions: {
        async storeTemplate(context, data) {
            const versions = context.state.templates
                .filter((t) => t.data.key === data.key)
                .map((t) => t.data.version);
            const doc = await database.collection(ContractTemplatesCollection).add({
                ...data,
                version: (versions.length ? Math.max(...versions) : 0) + 1,
                published: false,
                requiresResign: false,
                createdAt: firebase.firestore.Timestamp.now(),
                createdBy: store.getters['user/userId'],
                deletedAt: null,
            });
            return doc.id;
        },
        async publishTemplate(context, data) {
            await callFn('publishTemplate', data);
        },
        async destroyTemplate(context, template) {
            await database.collection(ContractTemplatesCollection).doc(template.id).update({
                deletedAt: firebase.firestore.Timestamp.now(),
            });
        },
        async sign(context, data) {
            const { id } = await callFn('signContract', data);
            // The onSnapshot listener lags behind the callable; store the contract now so the schedule guard passes immediately.
            const doc = await database.collection(ContractsCollection).doc(id).get();
            context.commit('storeContract', { id: doc.id, data: doc.data() });
        },
        async countersign(context, data) {
            await callFn('countersignContract', data);
        },
        async invalidate(context, contractId) {
            await callFn('invalidateContract', { contractId });
        },
        async pdfUrl(context, contract) {
            if (!contract.data.pdfPath)
                throw new Error('PDF není k dispozici');
            return firebase.storage().ref(contract.data.pdfPath).getDownloadURL();
        },
        async listen(context, data) {
            context.commit('clear');
            const templatesUnsub = database.collection(ContractTemplatesCollection).onSnapshot(snapshot => {
                for (const change of snapshot.docChanges()) {
                    if (change.type === 'removed') {
                        context.commit('removeTemplate', change.doc.id);
                    }
                    else {
                        context.commit('storeTemplate', { id: change.doc.id, data: change.doc.data() });
                    }
                }
            });
            let query = database.collection(ContractsCollection);
            if (!data.isModerator) {
                query = query.where('instructorId', '==', data.userId);
            }
            const contractsUnsub = query.onSnapshot(snapshot => {
                for (const change of snapshot.docChanges()) {
                    if (change.type === 'removed') {
                        context.commit('removeContract', change.doc.id);
                    }
                    else {
                        context.commit('storeContract', { id: change.doc.id, data: change.doc.data() });
                    }
                }
            });
            return [templatesUnsub, contractsUnsub];
        },
    },
    getters: {
        templates(state) {
            return state.templates
                .filter(t => !t.data.deletedAt)
                .sort((a, b) => a.data.key.localeCompare(b.data.key) || b.data.version - a.data.version);
        },
        templateFor(state) {
            return (key, date) => state.templates.find(t => t.data.key === key
                && t.data.published
                && !t.data.deletedAt
                && coversDate(t.data, date));
        },
        /** Keys whose published template covers the day while the instructor has no contract for it yet. */
        pendingFor(state) {
            return (uid, keys, date) => keys.filter(key => {
                const covering = state.templates.filter(t => t.data.key === key && !t.data.deletedAt && coversDate(t.data, date));
                if (!covering.some(t => t.data.published))
                    return false;
                // Earlier versions of the same period still count, same as the backend guard.
                const coveringIds = new Set(covering.map(t => t.id));
                return !state.contracts.some(c => c.data.instructorId === uid
                    && c.data.status !== 'invalidated'
                    && coveringIds.has(c.data.templateId));
            });
        },
        contracts(state) {
            return state.contracts;
        },
        /** With a date, only a contract whose template covers that day counts; earlier versions of the period count too. */
        activeContractFor(state) {
            return (uid, date) => state.contracts.find(c => {
                if (c.data.instructorId !== uid)
                    return false;
                if (c.data.status === 'invalidated')
                    return false;
                if (!date)
                    return true;
                const template = state.templates.find(t => t.id === c.data.templateId);
                return !!template && coversDate(template.data, date);
            });
        },
        pendingCountersign(state) {
            return state.contracts.filter(c => c.data.status === 'signed_by_instructor');
        },
    },
};
